import { useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getFormattedDate } from 'lib/common';
import { getEventMessage } from 'lib/events';
import { Link, TimeIndicator, TitleWithIcon, User } from 'components/common';
import { Icon } from 'components/common/icons';
import { EventLogEntryData } from '.';

import type { GetEventsRes } from 'shared-types/responses';

interface Props {
  event: GetEventsRes['events'][0];
  includeCalendarEvent?: boolean;
  includeCourse?: boolean;
  includePath?: boolean;
  showEventData?: boolean;
  showUserAvatar?: boolean;
}

const EventLogEntry = ({
  event,
  includeCalendarEvent,
  includeCourse,
  includePath,
  showEventData,
  showUserAvatar = true
}: Props) => {
  const { language, messages, serverConfig, theme } = useReduxState();

  const [dataVisible, setDataVisible] = useState(false);

  return (
    <div className="event">
      <div className="row-sm row-top">
        <div className="col">
          <div className="content">
            {showUserAvatar ? (
              <div className="user">
                {event.eventUser ? (
                  <User showNameOrUsername={false} user={event.eventUser} />
                ) : event.integration ? (
                  <TitleWithIcon
                    avatarProps={{
                      fontSize: '1.5rem',
                      padding: '7px 0',
                      size: '30px'
                    }}
                    icon="share-alt"
                    iconStyle={{ margin: 0 }}
                    iconTooltip={messages.commonIntegration}
                    title=""
                  />
                ) : (
                  <User
                    showNameOrUsername={false}
                    anonymous={true}
                    user={null}
                  />
                )}
              </div>
            ) : null}

            <div className="description">
              <div
                className="user-title"
                {...(event.eventUser
                  ? {
                      'aria-label':
                        event.eventUser.email || event.eventUser.username,
                      'data-tooltip':
                        event.eventUser.email || event.eventUser.username
                    }
                  : {})}
              >
                {event.eventUser ? (
                  <Link
                    as={`/users/${event.eventUser.id}`}
                    href="/users/[userId]"
                    linkColor="inherit"
                    text={
                      serverConfig.userUseName &&
                      event.eventUser &&
                      event.eventUser.firstName &&
                      event.eventUser.lastName
                        ? `${event.eventUser.firstName} ${event.eventUser.lastName}`
                        : event.eventUser.username
                    }
                  />
                ) : /external-api-.*/.test(event.data?.eventUserSource) ? (
                  messages.settingsExternalApi
                ) : event.integration ? (
                  event.integration.title
                ) : (
                  messages.commonAnonymous
                )}
              </div>{' '}
              {getEventMessage({
                includeCalendarEvent,
                includeCourse,
                includePath,
                event,
                messages
              })}
            </div>
          </div>
        </div>
        <div className="col-min col-no-top-margin">
          <div className="info">
            {showEventData ? (
              <div
                className="toggle-data"
                onClick={() => setDataVisible(!dataVisible)}
                role="button"
                tabIndex={0}
              >
                {messages.activitiesCommonShowData}
                <Icon
                  icon={dataVisible ? 'chevron-up' : 'chevron-down'}
                  style={{ fontSize: '1.2rem', marginLeft: 5 }}
                />
              </div>
            ) : null}
            <div
              aria-label={getFormattedDate({
                date: event.createdAt,
                dateFormat: 'dd.MM.y HH:mm'
              })}
              className="time"
              data-tooltip={getFormattedDate({
                date: event.createdAt,
                dateFormat: 'dd.MM.y HH:mm'
              })}
            >
              <Icon
                icon={['far', 'clock']}
                style={{
                  fontSize: '1.1rem',
                  marginRight: '5px'
                }}
              />
              <TimeIndicator
                language={language}
                timeString={event.createdAt as string}
              />
            </div>
          </div>
        </div>
      </div>
      {showEventData ? (
        <EventLogEntryData data={event.data} visible={dataVisible} />
      ) : null}

      <style jsx>{`
        .content {
          display: flex;
        }
        .user {
          display: inline-block;
          margin-right: 5px;
        }
        .user-title {
          color: ${theme.textColorLight};
          display: inline-block;
          font-size: 1.5rem;
          font-weight: 600;
        }
        .description {
          color: ${theme.textColorLighter};
          display: inline-block;
          font-size: 1.5rem;
          font-weight: 400;
          margin-top: ${showUserAvatar ? '5px' : '0'};
          margin-left: ${showUserAvatar ? '4px' : '0'};
        }
        .toggle-data {
          color: ${theme.textColorLighter};
          cursor: pointer;
          display: inline-block;
          font-size: 1.4rem;
          margin-right: 15px;
        }
        .time {
          color: ${theme.textColorLighter};
          display: inline-block;
          font-size: 1.4rem;
          font-weight: 400;
          margin-top: ${showUserAvatar ? '6px' : '1px'};
          white-space: nowrap;
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .info {
            margin-left: ${showUserAvatar ? '37px' : '0'};
          }
        }
      `}</style>
    </div>
  );
};

export default EventLogEntry;
